import React from "react";
import { useGetBusinesses } from "@/hooks/useGetBusinesses";
import { useBudgetContext } from "./useBudgetContext";
import { BudgetDialog } from "./BudgetDialog";
import { GetBudgetResolutionEnum } from "@norma-bi/bi-api";
import { isAllBusiness } from "@/api";
import { Dropdown, DropdownOption } from "@/components/Dropdown";
import * as Sentry from "@sentry/react";
import { useAuth } from "@/Auth";

const RESOLUTION_OPTIONS = [
  { id: GetBudgetResolutionEnum.monthly, label: "Monthly" },
  { id: GetBudgetResolutionEnum.weekly, label: "Weekly" },
] as const;

export function BudgetHeader() {
  const isDemo = useAuth().user?.is_demo ?? false;
  const [showEditDialog, setShowEditDialog] = React.useState(false);

  const {
    selectedYear,
    setSelectedYear,
    years,
    setSelectedBusinessId,
    selectedBusinessId,
    budgetData,
    selectedResolution,
    setSelectedResolution,
  } = useBudgetContext();
  const { data: businesses } = useGetBusinesses();

  const handleBusinessSelect = React.useCallback(
    (businessId: string) => {
      if (businessId === "ALL") {
        console.error("WARNING: Attempting to set selectedBusinessId to ALL!");
        Sentry.captureEvent({
          message: "Budget context selectedBusinessId set to ALL - investigating root cause",
          level: "error",
          tags: {
            component: "BudgetHeader",
            action: "handleBusinessSelect",
            issue: "ALL_BUSINESS_SELECTED",
          },
          extra: {
            businessId,
            businesses: businesses?.map((b) => ({ id: b.id, name: b.name })),
            selectedBusinessId,
          },
        });
        return;
      }
      setSelectedBusinessId(businessId);
    },
    [setSelectedBusinessId, businesses, selectedBusinessId],
  );

  React.useEffect(() => {
    if (businesses && !selectedBusinessId) {
      const firstNonAllBusiness = businesses.find((b) => !isAllBusiness(b.id));
      if (firstNonAllBusiness) {
        if (firstNonAllBusiness.id === "ALL") {
          console.error("WARNING: First non-ALL business is still ALL!");
          Sentry.captureEvent({
            message: "Budget context selectedBusinessId set to ALL - investigating root cause",
            level: "error",
            tags: {
              component: "BudgetHeader",
              action: "useEffect_autoSelect",
              issue: "ALL_BUSINESS_SELECTED",
            },
            extra: {
              businessId: firstNonAllBusiness.id,
              businesses: businesses?.map((b) => ({ id: b.id, name: b.name })),
              selectedBusinessId,
            },
          });
          return;
        }
        setSelectedBusinessId(firstNonAllBusiness.id);
      }
    }
  }, [businesses, selectedBusinessId, setSelectedBusinessId]);

  const businessOptions: DropdownOption<string>[] = React.useMemo(() => {
    return (
      businesses?.map((business) => ({
        id: business.id,
        label: business.name,
      })) || []
    );
  }, [businesses]);

  const yearOptions: DropdownOption<number>[] = React.useMemo(() => {
    return years.map((year) => ({
      id: year,
      label: year.toString(),
    }));
  }, [years]);

  const resolutionOptions: DropdownOption<GetBudgetResolutionEnum>[] = React.useMemo(() => {
    return RESOLUTION_OPTIONS.map((option) => ({
      id: option.id,
      label: option.label,
    }));
  }, []);

  const showResolutionAndEdit = budgetData && budgetData.venue_budgets.length > 0;

  return (
    <div className="flex flex-col mb-8">
      <div className="text-xl font-medium text-feintGray mb-6">Budget</div>

      <div className="flex justify-between items-center">
        {showResolutionAndEdit && !isDemo && (
          <button
            onClick={() => setShowEditDialog(true)}
            className="px-4 py-2 bg-pureWhite text-feintGray hover:bg-backgroundGray hover:text-dark rounded-lg text-sm"
          >
            Edit
          </button>
        )}
        {!showResolutionAndEdit && <div />}

        <div className="flex gap-6">
          <Dropdown
            options={yearOptions}
            selectedOption={selectedYear}
            onSelect={setSelectedYear}
          />
          {showResolutionAndEdit && (
            <Dropdown
              options={resolutionOptions}
              selectedOption={selectedResolution}
              onSelect={setSelectedResolution}
            />
          )}

          <Dropdown
            options={businessOptions}
            selectedOption={selectedBusinessId || ""}
            onSelect={handleBusinessSelect}
            placeholder="Select Business"
            alignment="right"
          />
        </div>
      </div>

      {showEditDialog && !isDemo && (
        <BudgetDialog
          show={showEditDialog}
          mode="edit"
          onClose={() => setShowEditDialog(false)}
          businessId={selectedBusinessId || ""}
          year={selectedYear}
        />
      )}
    </div>
  );
}
